<template>
	<section v-if="statistics">
		<el-row>
			<el-col :sm="12" :xs="24">
				<h2>Entscheidung Statistik</h2>
			</el-col>
			<el-col :sm="12" :xs="24" class="flex flex--justifyend" v-if="jsonData">
				<download-csv
					:data="jsonData"
					:labels="{ decision: 'Entscheidung', werberat: 'Werberat', jungerWerberat: 'Junger Werberat', kleinerSenat: 'Kleiner Senat' }"
					:name="csvName"
					class="el-button el-button--primary is-round"
				>
					Statistikdaten exportieren
				</download-csv>
			</el-col>
		</el-row>

		<template v-if="viableDecisions.length > 0">
			<div class="charts-wrapper">
				<PieChart
					v-if="type === DecisionType.SmallSenate"
					:decisions="smallSenateDecisions"
					:title="title || 'Kleiner Senat'"
					:type="DecisionType.SmallSenate"
				/>

				<template v-else-if="type === DecisionType.Werberat">
					<PieChart
						v-if="hasJungerWerberatDecisions"
						:decisions="jungerWerberatDecisions"
						:title="'Junger Werberat'"
						:type="DecisionType.JungerWerberat"
					/>
					<PieChart :decisions="werberatDecisions" :title="'Werberat'" :type="DecisionType.Werberat" />
					<PieChart
						v-if="jungerWerberatDecisions?.length > 0 && werberatDecisions?.length > 0"
						:decisions="totalDecisions"
						:title="'Gesamt'"
						:type="DecisionType.Werberat"
					/>
				</template>

				<PieChart
					v-if="type === DecisionType.JungerWerberat"
					:decisions="jungerWerberatDecisions"
					:title="'Junger Werberat'"
					:type="DecisionType.JungerWerberat"
				/>
			</div>

			<ChartLegend :type="type" />

			<div class="wahlbeteiligung-wrapper">
				<span>Wahlbeteiligung: </span>

				<Wahlbeteiligung v-if="type === DecisionType.SmallSenate" :decisionsData="smallSenateDecisions" :title="title || 'Kleiner Senat'" />
				<template v-else-if="type === DecisionType.Werberat">
					<Wahlbeteiligung
						v-for="(role, index) in parsedWerberatRoles"
						:key="'wahlbeteiligung-' + index"
						:decisionsData="role.decisions"
						:title="role.name"
					/>
				</template>
				<Wahlbeteiligung v-if="type === DecisionType.JungerWerberat" :decisionsData="jungerWerberatDecisions" :title="'Junger Werberat'" />
			</div>

			<template v-if="type === DecisionType.Werberat">
				<h2 class="statcategories__header">Top 10 genannte Kategorien (Werberäte)</h2>
				<CategoryBarChart :categoryData="werberatCategories" :totalCount="werberatDecisionsCount" />
			</template>
		</template>
		<template v-else>
			<p>Keine Entscheidungen vorhanden.</p>
		</template>
	</section>
</template>

<script lang="ts">
// DOCS:
// https://www.chartjs.org/docs/latest/configuration/
// https://vue-chartjs.org/examples/

import {
	DecisionState,
	DecisionType,
	RoleResponse,
	StatisticResponse,
	StatisticResponseWithCaseId,
	StatisticDecisionResponseItem,
} from '@/types/types';
import { defineComponent, PropType } from 'vue';
import { Pie } from 'vue-chartjs';
import TheButton from '@/components/TheButton.vue';
import PieChart from '@/components/charts/PieChart.vue';

import ChartLegend from '@/components/charts/ChartLegend.vue';
import Wahlbeteiligung from '@/components/charts/Wahlbeteiligung.vue';
import CategoryBarChart from '@/components/charts/CategoryBarChart.vue';
import { getParsedDecisions, getCurrentDecisions, getWerberatDecisionsCount } from '@/utils/statisticHandling/statistics';
import moment from 'moment';

interface ExportDecisionEntry {
	decision: string;
	werberat?: number;
	jungerWerberat?: number;
	kleinerSenat?: number;
}

export default defineComponent({
	name: 'SectionStatistics',
	data() {
		return {
			DecisionType,
			chartOptions: {
				responsive: true,
				plugins: {
					legend: {
						position: 'bottom',
					},
				},
			} as any,
		};
	},
	components: {
		Pie,
		PieChart,
		ChartLegend,
		Wahlbeteiligung,
		CategoryBarChart,
		TheButton,
	},
	props: {
		statistics: {
			type: Object as PropType<StatisticResponse | StatisticResponseWithCaseId | null>,
			required: true,
		},
		type: {
			type: String as PropType<DecisionType>,
			required: true,
		},
		title: {
			type: String,
		},
		caseHid: {
			type: String,
			required: true,
		},
	},
	computed: {
		smallSenateDecisions() {
			if (!this.statistics) return [];
			return getCurrentDecisions(this.statistics, DecisionType.SmallSenate);
		},
		jungerWerberatDecisions() {
			if (!this.statistics) return [];
			return getCurrentDecisions(this.statistics, DecisionType.JungerWerberat);
		},
		hasJungerWerberatDecisions() {
			if (!this.statistics) return false;
			return this.parsedWerberatRoles.some((role) => role.name === 'Junger Werberat' && role.decisions.length > 1);
		},

		werberatDecisions() {
			if (!this.statistics) return [];
			return getCurrentDecisions(this.statistics, DecisionType.Werberat);
		},
		totalDecisions() {
			if (!this.statistics) return [];
			return getCurrentDecisions(this.statistics);
		},
		viableDecisions() {
			if (!this.statistics) return [];

			// get all decisions if Werberat
			const currentDecisions = getCurrentDecisions(this.statistics, this.type === DecisionType.Werberat ? null : this.type);
			if (!currentDecisions) return [];

			return currentDecisions?.filter(
				(decision) => decision.decisionState !== DecisionState.Null && decision.decisionState !== DecisionState.UserAbsent,
			);
		},
		parsedWerberatRoles() {
			if (!this.statistics) return null;

			const parsedWerberat = getParsedDecisions(this.statistics, DecisionType.Werberat) as StatisticDecisionResponseItem[] | null;
			const parsedJungerWerberat = getParsedDecisions(this.statistics, DecisionType.JungerWerberat) as StatisticDecisionResponseItem[] | null;

			let roles = [] as any;
			if (parsedJungerWerberat && parsedJungerWerberat.length > 0) {
				roles.push({
					name: 'Junger Werberat',
					decisions: parsedJungerWerberat,
				});
			}

			if (parsedWerberat && parsedWerberat.length > 0) {
				roles.push({
					name: 'Werberat',
					decisions: parsedWerberat,
				});
			}

			if (roles.length > 1) {
				roles.push({
					name: 'Gesamt',
					decisions: (parsedWerberat ?? []).concat(parsedJungerWerberat ?? []),
				});
			}

			return roles;
		},
		werberatDecisionsCount() {
			if (!this.statistics) return 0;
			return getWerberatDecisionsCount(this.statistics);
		},
		werberatCategories() {
			if (!this.statistics) return [];
			return this.statistics.categories.filter((category) => category.decisionType === DecisionType.Werberat);
		},
		jsonData() {
			if (this.viableDecisions?.length == 0) return null;

			let jsonData = this.viableDecisions
				.reduce<ExportDecisionEntry[]>((acc, d) => {
					const decisionState = this.$getStateFullLabel('decision', d.decisionState, false).replace('Entscheidung: ', '');

					// Initialize entry if it doesn't exist
					let entry = acc.find((item) => item.decision === decisionState);
					if (!entry) {
						entry = {
							decision: decisionState,
							werberat: 0,
							jungerWerberat: 0,
							kleinerSenat: 0,
						};
						acc.push(entry);
					}

					// Increment the appropriate count based on decision type
					if (d.decisionType === DecisionType.Werberat && entry.werberat !== undefined) {
						entry.werberat += d.count;
					} else if (d.decisionType === DecisionType.JungerWerberat && entry.jungerWerberat !== undefined) {
						entry.jungerWerberat += d.count;
					} else if (d.decisionType === DecisionType.SmallSenate && entry.kleinerSenat !== undefined) {
						entry.kleinerSenat += d.count;
					}

					return acc;
				}, [])
				.map((item) => {
					let result: any = { decision: item.decision };

					// Show fields based on the current type
					if (this.type === DecisionType.Werberat) {
						result.werberat = item.werberat || '';
						result.jungerWerberat = item.jungerWerberat || '';
					} else if (this.type === DecisionType.JungerWerberat) {
						result.jungerWerberat = item.jungerWerberat || '';
					} else if (this.type === DecisionType.SmallSenate) {
						result.kleinerSenat = item.kleinerSenat || '';
					}

					return result;
				});

			return jsonData;
		},
		csvName() {
			let hid = '';
			if (this.caseHid) hid = '_' + this.caseHid;
			else hid = '';

			const datetime = '_' + moment().format('DD-MM-YYYY');
			return 'Statistikdaten' + hid + datetime + '.csv';
		},
	},
	watch: {
		statistics: {
			immediate: true,
			handler() {},
		},
	},
});
</script>
